import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import {
    ArrowLeft,
    BookOpen,
    Stethoscope,
    TestTube2,
    Pill,
    Leaf,
    UtensilsCrossed,
    Loader2,
    AlertCircle,
    Heart,
    Bone,
    Brain,
    Flame,
    Shield,
    Droplets,
    Wheat,
    Activity,
    Sparkles,
    Eye,
    Dna
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Condition metadata (same as KnowledgeBase.js)
const conditionsMeta = {
    'lupus': {
        name: 'Lupus (SLE)',
        fullName: 'Systemic Lupus Erythematosus',
        icon: Sparkles,
        color: 'from-purple-500 to-pink-500',
        bgColor: 'bg-purple-100 dark:bg-purple-900/30',
        textColor: 'text-purple-700 dark:text-purple-300'
    },
    'rheumatoid-arthritis': {
        name: 'Rheumatoid Arthritis',
        fullName: 'Rheumatoid Arthritis',
        icon: Bone,
        color: 'from-orange-500 to-red-500',
        bgColor: 'bg-orange-100 dark:bg-orange-900/30',
        textColor: 'text-orange-700 dark:text-orange-300'
    },
    'multiple-sclerosis': {
        name: 'Multiple Sclerosis (MS)',
        fullName: 'Multiple Sclerosis',
        icon: Brain,
        color: 'from-blue-500 to-cyan-500',
        bgColor: 'bg-blue-100 dark:bg-blue-900/30',
        textColor: 'text-blue-700 dark:text-blue-300'
    },
    'hashimotos': {
        name: "Hashimoto's Thyroiditis",
        fullName: "Hashimoto's Thyroiditis",
        icon: Shield,
        color: 'from-teal-500 to-emerald-500',
        bgColor: 'bg-teal-100 dark:bg-teal-900/30',
        textColor: 'text-teal-700 dark:text-teal-300'
    },
    'graves-disease': {
        name: "Graves' Disease",
        fullName: "Graves' Disease",
        icon: Flame,
        color: 'from-amber-500 to-orange-500',
        bgColor: 'bg-amber-100 dark:bg-amber-900/30',
        textColor: 'text-amber-700 dark:text-amber-300'
    },
    'ibd': {
        name: "Crohn's / Ulcerative Colitis",
        fullName: 'Inflammatory Bowel Disease (IBD)',
        icon: Activity,
        color: 'from-rose-500 to-pink-500',
        bgColor: 'bg-rose-100 dark:bg-rose-900/30',
        textColor: 'text-rose-700 dark:text-rose-300'
    },
    'celiac': {
        name: 'Celiac Disease',
        fullName: 'Celiac Disease',
        icon: Wheat,
        color: 'from-yellow-500 to-amber-500',
        bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
        textColor: 'text-yellow-700 dark:text-yellow-300'
    },
    'psoriasis': {
        name: 'Psoriasis / Psoriatic Arthritis',
        fullName: 'Psoriasis and Psoriatic Arthritis',
        icon: Droplets,
        color: 'from-indigo-500 to-purple-500',
        bgColor: 'bg-indigo-100 dark:bg-indigo-900/30',
        textColor: 'text-indigo-700 dark:text-indigo-300'
    },
    'type1-diabetes': {
        name: 'Type 1 Diabetes',
        fullName: 'Type 1 Diabetes Mellitus',
        icon: Dna,
        color: 'from-sky-500 to-blue-500',
        bgColor: 'bg-sky-100 dark:bg-sky-900/30',
        textColor: 'text-sky-700 dark:text-sky-300'
    },
    'sjogrens': {
        name: "Sjögren's Syndrome",
        fullName: "Sjögren's Syndrome",
        icon: Eye,
        color: 'from-cyan-500 to-teal-500',
        bgColor: 'bg-cyan-100 dark:bg-cyan-900/30',
        textColor: 'text-cyan-700 dark:text-cyan-300'
    },
    'ankylosing-spondylitis': {
        name: 'Ankylosing Spondylitis',
        fullName: 'Ankylosing Spondylitis',
        icon: Heart,
        color: 'from-red-500 to-rose-500',
        bgColor: 'bg-red-100 dark:bg-red-900/30',
        textColor: 'text-red-700 dark:text-red-300'
    }
};

const tabs = [
    { id: 'about', label: 'About', icon: BookOpen },
    { id: 'symptoms', label: 'Symptoms', icon: Stethoscope },
    { id: 'testing', label: 'Triggers & Testing', icon: TestTube2 },
    { id: 'supplements', label: 'Supplements', icon: Pill },
    { id: 'lifestyle', label: 'Lifestyle', icon: Leaf },
    { id: 'diet', label: 'Diet', icon: UtensilsCrossed }
];

export default function ConditionPage() {
    const { conditionId } = useParams();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('about');
    const [content, setContent] = useState(null);
    const [loading, setLoading] = useState(true);

    const condition = conditionsMeta[conditionId];

    useEffect(() => {
        if (!condition) {
            navigate('/knowledge');
            return;
        }
        fetchConditionContent();
    }, [conditionId]);

    const fetchConditionContent = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_URL}/api/knowledge/conditions/${conditionId}`);
            setContent(response.data);
        } catch (error) {
            // If no content exists yet, use placeholder
            setContent(null);
        } finally {
            setLoading(false);
        }
    };

    if (!condition) {
        return null;
    }

    const Icon = condition.icon;

    const renderTabContent = (tabId) => {
        const tabContent = content?.tabs?.[tabId];
        
        if (!tabContent || !tabContent.content) {
            return (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className={`w-16 h-16 rounded-2xl ${condition.bgColor} flex items-center justify-center mb-4`}>
                        <AlertCircle className={`w-8 h-8 ${condition.textColor}`} />
                    </div>
                    <h3 className="font-heading font-semibold text-lg mb-2">Content Coming Soon</h3>
                    <p className="text-muted-foreground max-w-md">
                        We're working on adding comprehensive information about {tabs.find(t => t.id === tabId)?.label.toLowerCase()} for {condition.name}. 
                        Check back soon!
                    </p>
                </div>
            );
        }

        return (
            <div className="prose prose-slate dark:prose-invert max-w-none prose-headings:font-heading prose-h2:text-xl prose-h2:mt-6 prose-h2:mb-3 prose-p:text-muted-foreground prose-li:text-muted-foreground prose-strong:text-foreground">
                <div dangerouslySetInnerHTML={{ __html: tabContent.content }} />
            </div>
        );
    };

    return (
        <div className="space-y-6 animate-fade-in" data-testid="condition-page">
            {/* Back Button */}
            <Button
                variant="ghost"
                onClick={() => navigate('/knowledge')}
                className="gap-2 -ml-2"
                data-testid="back-to-knowledge"
            >
                <ArrowLeft className="w-4 h-4" />
                Back to Knowledge Base
            </Button>

            {/* Header */}
            <div className="flex items-start gap-6">
                <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${condition.color} flex items-center justify-center flex-shrink-0 shadow-lg`}>
                    <Icon className="w-10 h-10 text-white" />
                </div>
                <div>
                    <h1 className="font-heading text-3xl md:text-4xl font-bold">
                        {condition.name}
                    </h1>
                    <p className="text-lg text-muted-foreground mt-1">
                        {condition.fullName}
                    </p>
                    {content?.last_updated && (
                        <p className="text-sm text-muted-foreground mt-2">
                            Last updated: {new Date(content.last_updated).toLocaleDateString()}
                        </p>
                    )}
                </div>
            </div>

            <Separator />

            {/* Tabs */}
            {loading ? (
                <div className="flex items-center justify-center py-16">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
            ) : (
                <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                    <TabsList className="flex flex-wrap h-auto gap-2 bg-transparent p-0">
                        {tabs.map((tab) => {
                            const TabIcon = tab.icon;
                            return (
                                <TabsTrigger
                                    key={tab.id}
                                    value={tab.id}
                                    className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-full px-4 py-2 gap-2"
                                    data-testid={`tab-${tab.id}`}
                                >
                                    <TabIcon className="w-4 h-4" />
                                    {tab.label}
                                </TabsTrigger>
                            );
                        })}
                    </TabsList>

                    {tabs.map((tab) => (
                        <TabsContent key={tab.id} value={tab.id} className="mt-6">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="font-heading flex items-center gap-2">
                                        <tab.icon className="w-5 h-5 text-primary" />
                                        {tab.label}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {renderTabContent(tab.id)}
                                </CardContent>
                            </Card>
                        </TabsContent>
                    ))}
                </Tabs>
            )}

            {/* Disclaimer */}
            <Card className="bg-muted/50 border-muted">
                <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground">
                        <strong>Disclaimer:</strong> This information is for educational purposes only and should not replace professional medical advice. 
                        Always consult with your healthcare provider before making any changes to your treatment plan.
                    </p>
                </CardContent>
            </Card>
        </div>
    );
}
